import tracking from "../../tracking/Tracking";
import { SfidWrapper } from "./SfidWrapper";

export type ResolvedSelectionResponse = {
  html: string;
  rule: string;
  url: string;
  count?: number;
};

export type SelectionResolver = (
  params: URLSearchParams,
  operationSequence: number,
) => Promise<ResolvedSelectionResponse>;

export type SelectionDomAdapter = {
  bootstrapRule(): string;
  currentUrl(): string;
  historyEntryRule(): string | undefined;
  applyResolvedResponse(response: ResolvedSelectionResponse): void;
  currentSsid(): string | undefined;
};

const FILTER_RESOLVER_ENDPOINT = "/grasshopper/filter/resolve/dreson";

export function createProductionSelectionDependencies(): {
  resolver: SelectionResolver;
  dom: SelectionDomAdapter;
} {
  return {
    resolver: resolveSelection,
    dom: browserSelectionDom,
  };
}

const browserSelectionDom: SelectionDomAdapter = {
  bootstrapRule() {
    const rule = document.getElementById("avContent")?.dataset.rule;
    if (!rule) {
      throw new Error("Failed to bootstrap selection: missing rule");
    }
    return rule;
  },
  currentUrl() {
    return window.location.href;
  },
  historyEntryRule() {
    return window.history.state?.entryRule;
  },
  applyResolvedResponse(response) {
    const sfidWrapper = document.getElementById("reptile-sfid-wrapper");
    if (sfidWrapper) {
      sfidWrapper.innerHTML = response.html;
    }
    tracking.replaceContext("reptile-sfid-wrapper", response.url);
  },
  currentSsid() {
    return SfidWrapper.ssid();
  },
};

async function resolveSelection(params: URLSearchParams): Promise<ResolvedSelectionResponse> {
  const response = await fetch(`${FILTER_RESOLVER_ENDPOINT}?${params.toString()}`, {
    headers: {
      "X-Find-Trackable": "ts_Assert=none",
    },
  });

  if (response.status !== 200) {
    throw new Error(`Failed to resolve rule: ${response.status} ${response.statusText}`);
  }

  const html = await response.text();
  const metadata = parseResolverResponse(html);
  return { html, ...metadata };
}

function parseResolverResponse(html: string): Omit<ResolvedSelectionResponse, "html"> {
  const document = new DOMParser().parseFromString(html, "text/html");
  const element = document.querySelector<HTMLElement>("#avContent");
  const rule = element?.dataset.rule;
  const resolvedUrl = element?.dataset.url || element?.dataset.redirect;
  const rawCount = element?.dataset.count;

  if (!rule || !resolvedUrl) {
    throw new Error("Failed to resolve rule: missing url or rule");
  }

  const count = rawCount === undefined || rawCount.length === 0 ? undefined : Number.parseInt(rawCount);
  if (count !== undefined && Number.isNaN(count)) {
    throw new Error("Failed to resolve rule: invalid count");
  }

  return {
    rule,
    url: new URL(resolvedUrl, window.location.origin).href,
    ...(count !== undefined ? { count } : {}),
  };
}
